import { lazy, Suspense } from "react";
import { ClientOnly } from "@tanstack/react-router";
import { Nav } from "@/components/festival/Nav";
import { Hero } from "@/components/festival/Hero";
import { Why } from "@/components/festival/Why";
import { FilmProgram } from "@/components/festival/FilmProgram";
import { EmailSignup } from "@/components/festival/EmailSignup";
import { Gallery } from "@/components/festival/Gallery";
import { Practical } from "@/components/festival/Practical";
import { Travel } from "@/components/festival/Travel";
import { Weather } from "@/components/festival/Weather";
import { Accessibility } from "@/components/festival/Accessibility";
import { FAQ } from "@/components/festival/FAQ";
import { FinalCTA } from "@/components/festival/FinalCTA";
import { SponsorStrip } from "@/components/festival/SponsorStrip";
import { Footer } from "@/components/festival/Footer";
import { I18nProvider } from "@/i18n/I18nProvider";
import { getDict, SITE_URL, pathForLang, LANGS, type Lang } from "@/i18n/translations";
import heroDesktop from "@/assets/uploads/hero-desktop.webp.asset.json";
import heroMobile from "@/assets/uploads/hero-mobile.webp.asset.json";

// Below-the-fold, client-only interactive bits — defer to a lazy chunk so they
// don't ship with the initial bundle.
const StickyMobileCTA = lazy(() =>
  import("@/components/festival/StickyMobileCTA").then((m) => ({ default: m.StickyMobileCTA })),
);
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

export function FestivalPage({ lang }: { lang: Lang }) {
  return (
    <I18nProvider lang={lang}>
      <div className="min-h-screen bg-background pb-16 md:pb-0">
        <Nav />
        <main>
          <Hero />
          <Why />
          <FilmProgram />
          <EmailSignup />
          <Gallery />
          <Practical />
          <Travel />
          <Weather />
          <Accessibility />
          <FAQ />
          <FinalCTA />
        </main>
        <SponsorStrip />
        <Footer />
        <ClientOnly fallback={null}>
          <Suspense fallback={null}>
            <StickyMobileCTA />
            <Toaster position="top-center" />
          </Suspense>
        </ClientOnly>
      </div>
    </I18nProvider>
  );
}

export function buildHomeHead(lang: Lang) {
  const t = getDict(lang);
  const url = `${SITE_URL}${pathForLang(lang)}`;
  const alternates = LANGS.map((l) => ({
    rel: "alternate",
    hrefLang: l as string,
    href: `${SITE_URL}${pathForLang(l)}`,
  }));
  alternates.push({ rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}/` });

  const eventJsonLd = {
    "@context": "https://schema.org",
    "@type": "Festival",
    name: "Openluchtfilmfestival Apeldoorn",
    startDate: "2026-08-14",
    endDate: "2026-08-22",
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: t.seo.home.description,
    image: ["https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/da2fb54f-6014-4784-8914-3cb47d243937/id-preview-3f6ccb2c--434dbadd-3be0-4546-a03f-f6327e5ee906.lovable.app-1780500398822.png"],
    location: {
      "@type": "Place",
      name: "Stadspark Berg & Bos",
      address: {
        "@type": "PostalAddress",
        streetAddress: "J.C. Wilslaan",
        addressLocality: "Apeldoorn",
        addressCountry: "NL",
      },
    },
    organizer: {
      "@type": "Organization",
      name: "Openluchtfilmfestival Apeldoorn",
      url: SITE_URL,
    },
    url,
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: t.faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Openluchtfilmfestival Apeldoorn",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon-180.png`,
  };

  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "EntertainmentBusiness",
    name: "Openluchtfilmfestival Apeldoorn",
    url: SITE_URL,
    image: `${SITE_URL}/favicon-180.png`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "J.C. Wilslaan",
      addressLocality: "Apeldoorn",
      postalCode: "7313 HK",
      addressRegion: "Gelderland",
      addressCountry: "NL",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 52.2122,
      longitude: 5.9296,
    },
    areaServed: "Apeldoorn",
    openingHours: "Mo-Su 20:00-24:00",
  };

  return {
    meta: [
      { title: t.seo.home.title },
      { name: "description", content: t.seo.home.description },
      { property: "og:title", content: t.seo.home.ogTitle },
      { property: "og:description", content: t.seo.home.ogDescription },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      { property: "og:locale", content: lang === "nl" ? "nl_NL" : lang === "de" ? "de_DE" : "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: t.seo.home.ogTitle },
      { name: "twitter:description", content: t.seo.home.ogDescription },
    ],
    links: [
      { rel: "preload", as: "image", href: heroMobile.url, type: "image/webp", media: "(max-width: 767px)", fetchPriority: "high" as const },
      { rel: "preload", as: "image", href: heroDesktop.url, type: "image/webp", media: "(min-width: 768px)", fetchPriority: "high" as const },
      { rel: "canonical", href: url },
      ...alternates,
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(eventJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(orgJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd) },
    ],
  };
}